/**
 * Phase 1 markdown subset per CHAT_SYSTEM §9 / CLIENT_DESIGN §9.
 *
 * Supported:
 *   **bold**           → <strong>bold</strong>
 *   *italic*           → <em>italic</em>
 *   `inline code`      → <code>inline code</code>
 *
 * No raw HTML is ever emitted — input is treated as plain text; the parser
 * returns a flat array of span-descriptor records that the React renderer
 * maps 1-to-1 to safe elements (<strong>, <em>, <code>, plain text). Any
 * unmatched marker falls through as literal text.
 *
 * Intentionally hand-rolled rather than pulling `marked` / `markdown-it`
 * in — the subset is three rules and the output is a React element tree,
 * so the security surface is minimal.
 */

export type SpanKind = 'text' | 'bold' | 'italic' | 'code';

export interface MarkdownSpan {
  kind: SpanKind;
  text: string;
}

/**
 * Tokenise `input` into a flat span list. Nested formatting is not
 * supported in Phase 1 (a `*` inside `**bold**` stays literal).
 */
export function parseMarkdown(input: string): MarkdownSpan[] {
  const out: MarkdownSpan[] = [];
  let i = 0;
  while (i < input.length) {
    // Inline code — greedy to next backtick on the same line.
    if (input[i] === '`') {
      const end = input.indexOf('`', i + 1);
      if (end > i) {
        out.push({ kind: 'code', text: input.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }

    // Bold `**...**` — check before italic so the outer `**` binds.
    if (input[i] === '*' && input[i + 1] === '*') {
      const end = input.indexOf('**', i + 2);
      if (end > i + 1) {
        out.push({ kind: 'bold', text: input.slice(i + 2, end) });
        i = end + 2;
        continue;
      }
    }

    // Italic `*...*` — single asterisk.
    if (input[i] === '*') {
      const end = input.indexOf('*', i + 1);
      if (end > i) {
        out.push({ kind: 'italic', text: input.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }

    // Plain-text run — append until the next marker.
    let j = i;
    while (
      j < input.length &&
      input[j] !== '*' &&
      input[j] !== '`'
    ) {
      j++;
    }
    if (j > i) {
      out.push({ kind: 'text', text: input.slice(i, j) });
      i = j;
    } else {
      // Unmatched marker — consume one char literally.
      out.push({ kind: 'text', text: input[i] ?? '' });
      i += 1;
    }
  }
  return out;
}
